import type { Capability, CapabilityDto, RoleDto, Section, SectionDto } from '@gsls/shared';
import { defaultSectionsFor } from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  InfoNote,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { DataTable, type DataColumn } from '../components/DataTable';
import { ApiError, api } from '../lib/api';

/**
 * Roles: a named set of capabilities, plus the workspace its view presents.
 *
 * This page is the only place the permission matrix can be changed, so it is
 * deliberately explicit about the two things that make that safe:
 *
 *  - a built-in role's capabilities cannot be changed here, because `admin` is
 *    the guaranteed way back into this page and a role that can re-scope itself
 *    is a role that can lock everyone out;
 *  - a capability the code does not know about cannot be granted, because the
 *    list is the code's own list rather than free text.
 *
 * The workspace (which screens a view shows) is a separate axis and stays
 * editable even on a built-in role: it decides what the sidebar lists and
 * secures nothing, so it cannot lock anyone out.
 */
export function RolesPage() {
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RoleDto | null>(null);

  const rolesQuery = useQuery({ queryKey: ['roles', {}], queryFn: () => api.roles.list() });
  const capabilitiesQuery = useQuery({
    queryKey: ['capabilities'],
    queryFn: api.roles.capabilities,
  });
  const sectionsQuery = useQuery({ queryKey: ['sections'], queryFn: api.roles.sections });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['roles'] });
    // A user's role name and the pickers on the Users page come from /roles too.
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    void queryClient.invalidateQueries({ queryKey: ['session'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.roles.remove(id),
    onSuccess: invalidate,
  });

  const roles = rolesQuery.data?.items ?? [];
  const capabilities = capabilitiesQuery.data?.items ?? [];
  const sections = sectionsQuery.data?.items ?? [];

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
    removeMutation.reset();
  }

  function openEdit(role: RoleDto) {
    setEditing(role);
    setFormOpen(true);
    removeMutation.reset();
  }

  const roleColumns: DataColumn<RoleDto>[] = [
    {
      key: 'role',
      header: 'Role',
      primary: true,
      cell: (role) => (
        <>
          <span className="font-medium text-ink">{role.name}</span>
          {role.description ? (
            <>
              <br />
              <span className="text-xs text-ink-muted">{role.description}</span>
            </>
          ) : null}
        </>
      ),
    },
    {
      key: 'code',
      header: 'Code',
      cell: (role) => <span className="font-mono text-xs">{role.code}</span>,
    },
    {
      key: 'capabilities',
      header: 'Capabilities',
      cell: (role) =>
        role.capabilities.length === 0 ? (
          <span className="text-xs text-ink-muted">
            none — can read, cannot change anything
          </span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {role.capabilities.map((capability) => (
              <span
                key={capability}
                className="rounded-sm bg-surface px-1.5 py-0.5 font-mono text-[11px] text-ink-body"
              >
                {capability}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: 'workspace',
      header: 'Workspace',
      cell: (role) => (
        <span
          className="text-xs text-ink-body"
          title={role.sections
            .map((code) => sections.find((s) => s.code === code)?.label ?? code)
            .join(', ')}
        >
          {role.sections.length === 0
            ? 'nothing'
            : `${role.sections.length} screen${role.sections.length === 1 ? '' : 's'}`}
        </span>
      ),
    },
    {
      key: 'users',
      header: 'Users',
      align: 'right',
      cell: (role) => role.userCount,
    },
    {
      key: 'type',
      header: 'Type',
      cell: (role) =>
        role.isSystem ? (
          <span className="inline-flex items-center gap-1.5">
            <Badge tone="muted">Built-in</Badge>
            <Tooltip
              subject="Built-in role"
              text="A built-in role cannot be renamed, re-scoped or deleted: admin in particular always holds every capability, which is what guarantees somebody can always manage access. Its workspace can still be re-arranged, because that only decides what the sidebar shows."
            />
          </span>
        ) : (
          <Badge tone="accent">Custom</Badge>
        ),
    },
  ];

  const roleActions = (role: RoleDto) => (
    <>
      <Button tone="ghost" onClick={() => openEdit(role)}>
        {role.isSystem ? 'Edit view' : 'Edit'}
      </Button>
      {role.isSystem ? null : (
        <Button
          tone="danger"
          disabled={removeMutation.isPending}
          title={role.userCount > 0 ? 'Move those users to another role first' : undefined}
          onClick={() => removeMutation.mutate(role.id)}
        >
          Delete
        </Button>
      )}
    </>
  );

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Roles"
        description="A role is a named set of capabilities, plus the workspace its view presents. The API checks capabilities on every request and the web app hides what you cannot use, so this page is the only place access is decided."
        actions={
          <Button onClick={openCreate} disabled={capabilities.length === 0}>
            New role
          </Button>
        }
      />

      {formOpen ? (
        <RoleForm
          editing={editing}
          capabilities={capabilities}
          sections={sections}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            invalidate();
            setFormOpen(false);
            setEditing(null);
          }}
        />
      ) : null}

      {removeMutation.error ? (
        <div className="mb-6">
          <ErrorNote>
            {removeMutation.error instanceof ApiError
              ? removeMutation.error.message
              : 'Could not delete that role.'}
          </ErrorNote>
        </div>
      ) : null}

      <Card>
        <CardBody>
          {rolesQuery.isPending || capabilitiesQuery.isPending || sectionsQuery.isPending ? (
            <Spinner />
          ) : null}
          <ErrorNote>
            {rolesQuery.isError || capabilitiesQuery.isError || sectionsQuery.isError
              ? 'Could not load roles. Is the API running?'
              : ''}
          </ErrorNote>

          {rolesQuery.isSuccess ? (
            <DataTable
              rows={roles}
              columns={roleColumns}
              rowKey={(role) => role.id}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actions={roleActions}
              empty="No roles found."
            />
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Create / edit                                                               */
/* -------------------------------------------------------------------------- */

function RoleForm({
  editing,
  capabilities,
  sections,
  onClose,
  onSaved,
}: {
  editing: RoleDto | null;
  capabilities: CapabilityDto[];
  sections: SectionDto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const locked = Boolean(editing?.isSystem);

  const [code, setCode] = useState(editing?.code ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [selected, setSelected] = useState<Capability[]>(editing?.capabilities ?? []);
  const [selectedSections, setSelectedSections] = useState<Section[]>(
    editing?.sections ?? defaultSectionsFor(editing?.capabilities ?? []),
  );

  /**
   * Once the administrator has touched the workspace by hand, ticking a
   * capability stops moving the sections with it. Until then it tracks the
   * capabilities, because forgetting to add the screen that goes with a
   * permission is the mistake this prevents — the role would work but its
   * sidebar would not offer it.
   */
  const [sectionsTouchedByHand, setSectionsTouchedByHand] = useState(false);

  const capabilityGroups = [...new Set(capabilities.map((capability) => capability.group))];
  const sectionGroups = [...new Set(sections.map((section) => section.group))];

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        // A built-in role's name and capabilities are fixed, so only the
        // workspace and the description are sent for one.
        await api.roles.update(
          editing.id,
          locked
            ? { description: description.trim() || null, sections: selectedSections }
            : {
                name: name.trim(),
                description: description.trim() || null,
                capabilities: selected,
                sections: selectedSections,
              },
        );
        return;
      }
      await api.roles.create({
        code: code.trim().toLowerCase(),
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        capabilities: selected,
        sections: selectedSections,
      });
    },
    onSuccess: onSaved,
  });

  function toggleCapability(capability: Capability) {
    setSelected((current) => {
      const next = current.includes(capability)
        ? current.filter((candidate) => candidate !== capability)
        : [...current, capability];
      if (!sectionsTouchedByHand) setSelectedSections(defaultSectionsFor(next));
      return next;
    });
  }

  function toggleGroup(group: string, on: boolean) {
    const inGroup = capabilities
      .filter((capability) => capability.group === group)
      .map((capability) => capability.code);
    setSelected((current) => {
      const next = on
        ? [...new Set([...current, ...inGroup])]
        : current.filter((capability) => !inGroup.includes(capability));
      if (!sectionsTouchedByHand) setSelectedSections(defaultSectionsFor(next));
      return next;
    });
  }

  function toggleSection(section: Section) {
    setSectionsTouchedByHand(true);
    setSelectedSections((current) =>
      current.includes(section)
        ? current.filter((candidate) => candidate !== section)
        : [...current, section],
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <Card className="mb-6">
      <CardBody>
        <h2 className="text-base font-bold text-ink">
          {editing ? (locked ? `Workspace for ${editing.name}` : `Edit ${editing.name}`) : 'New role'}
        </h2>
        <p className="mt-1 text-sm font-light text-ink-body">
          {locked
            ? "This is a built-in role: its capabilities cannot change, because admin holding every capability is what guarantees somebody can always manage access. Its workspace — which screens its view shows — can be re-arranged freely."
            : 'Pick the capabilities this role grants, and the screens its workspace presents. A role with no capabilities can still read everything — reads only need a signed-in session.'}
        </p>

        <form autoComplete="off" onSubmit={handleSubmit} className="mt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="Code"
              hint={editing ? 'Codes are immutable' : 'Lowercase letters, digits and underscore'}
              tooltip="The stable identifier used in audit entries, URLs and exports. It never changes, because renaming it would change what past records mean."
            >
              <input
                className={inputClass}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                disabled={Boolean(editing)}
                required
                placeholder="qa_reviewer"
                pattern="[a-z][a-z0-9_]{1,39}"
              />
            </Field>

            <Field label="Name" hint={locked ? 'Fixed on a built-in role' : undefined}>
              <input
                className={inputClass}
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={locked}
                required
                placeholder="QA Reviewer"
              />
            </Field>

            <Field label="Description" hint="Optional">
              <input
                className={inputClass}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Reviews certificates and batch records"
              />
            </Field>
          </div>

          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-ink-muted">
            Capabilities
            {locked ? (
              <span className="ml-2 normal-case tracking-normal text-ink-subtle">
                — fixed on a built-in role
              </span>
            ) : null}
          </h3>

          <div className="mt-2 space-y-4">
            {capabilityGroups.map((group) => {
              const inGroup = capabilities.filter((capability) => capability.group === group);
              const allSelected = inGroup.every((capability) =>
                selected.includes(capability.code),
              );

              return (
                <div key={group} className="rounded-md border border-surface-muted bg-canvas p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">
                      {group}
                    </p>
                    {locked ? null : (
                      <button
                        type="button"
                        className="text-xs font-bold uppercase tracking-wide text-brand-deep hover:underline"
                        onClick={() => toggleGroup(group, !allSelected)}
                      >
                        {allSelected ? 'Clear group' : 'Select group'}
                      </button>
                    )}
                  </div>

                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {inGroup.map((capability) => (
                      <label
                        key={capability.code}
                        className="flex items-start gap-2 text-sm text-ink-body"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={selected.includes(capability.code)}
                          disabled={locked}
                          onChange={() => toggleCapability(capability.code)}
                        />
                        <span>
                          <span className="font-semibold text-ink">{capability.label}</span>
                          <br />
                          <span className="text-xs text-ink-muted">{capability.description}</span>
                          <br />
                          <span className="font-mono text-[11px] text-ink-subtle">
                            {capability.code}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex items-baseline justify-between">
            <h3 className="text-sm font-bold uppercase tracking-wide text-ink-muted">
              Workspace
            </h3>
            <button
              type="button"
              className="text-xs font-bold uppercase tracking-wide text-brand-deep hover:underline"
              onClick={() => {
                setSectionsTouchedByHand(false);
                setSelectedSections(defaultSectionsFor(selected));
              }}
            >
              Suggest from capabilities
            </button>
          </div>
          <p className="mt-1 text-xs text-ink-muted">
            The screens this role&rsquo;s view shows. Presentation only — it grants nothing, and a
            screen it omits is still reachable if the role&rsquo;s capabilities allow it. Staying
            empty is fine too: the screen simply is not offered in that view.
          </p>

          <div className="mt-2 space-y-4">
            {sectionGroups.map((group) => {
              const inGroup = sections.filter((section) => section.group === group);
              const allSelected = inGroup.every((section) =>
                selectedSections.includes(section.code),
              );

              return (
                <div key={group} className="rounded-md border border-surface-muted bg-canvas p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">
                      {group}
                    </p>
                    <button
                      type="button"
                      className="text-xs font-bold uppercase tracking-wide text-brand-deep hover:underline"
                      onClick={() => {
                        setSectionsTouchedByHand(true);
                        const codes = inGroup.map((section) => section.code);
                        setSelectedSections((current) =>
                          allSelected
                            ? current.filter((section) => !codes.includes(section))
                            : [...new Set([...current, ...codes])],
                        );
                      }}
                    >
                      {allSelected ? 'Clear group' : 'Select group'}
                    </button>
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {inGroup.map((section) => (
                      <label
                        key={section.code}
                        className="flex items-center gap-2 text-sm text-ink-body"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSections.includes(section.code)}
                          onChange={() => toggleSection(section.code)}
                        />
                        <span>{section.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {sections.length > 0 && selectedSections.length === 0 ? (
            <p className="mt-3 text-xs text-ink-muted">
              Nothing ticked, so this view will offer no screens at all.
            </p>
          ) : null}

          {save.error ? (
            <div className="mt-4">
              <ErrorNote>
                {save.error instanceof ApiError ? save.error.message : 'Could not save the role.'}
              </ErrorNote>
            </div>
          ) : null}

          <div className="mt-4">
            <InfoNote>
              A change takes effect for everyone holding this role on their next action — sessions
              carry no copy of the capability set, so nobody has to sign in again.
            </InfoNote>
          </div>

          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending
                ? 'Saving…'
                : editing
                  ? locked
                    ? 'Save workspace'
                    : 'Save changes'
                  : 'Create role'}
            </Button>
            <Button tone="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
